import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import MemberNav from './MemberNav';
import { logError } from '../../utils/logger';
import { ordinal, speciesLabel } from '../../utils/memberStats';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import PaymentChoice from '../payment/PaymentChoice';
import PaymentReconciliationNotice from '../payment/PaymentReconciliationNotice';
import { getPreRegistrationWindowState, getPreRegistrationOpensAt } from '@shared/competitions';
import { deriveLifecycle, describeLifecycle, isPublished } from '@shared/competitionRules';
import { formatClubDate, formatClubTime } from '../../utils/clubTime';

const client = generateClient({ authMode: 'userPool' });

interface CompetitionsListProps {
  onNavigate: (
    page:
      | 'dashboard'
      | 'profile'
      | 'competitions'
      | 'catches'
      | 'my-stats'
      | 'my-fishing'
      | 'season-championship'
      | 'home'
  ) => void;
}

const CompetitionsList: React.FC<CompetitionsListProps> = ({ onNavigate }) => {
  const { userId } = useAuth();

  const [competitions, setCompetitions] = useState<any[]>([]);
  const [myRegistrations, setMyRegistrations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [registering, setRegistering] = useState<string | null>(null);
  const [pendingPaymentReg, setPendingPaymentReg] = useState<{
    id: string;
    eventId: string;
  } | null>(null);
  // Backlog M10. Kept SEPARATE from pendingPaymentReg (rather than clearing it
  // on success) so the modal stays open showing this notice instead of
  // auto-closing — the normal success/pay-on-day paths clear pendingPaymentReg
  // and fire a toast that auto-dismisses after 4s, which is the wrong UX for a
  // message telling someone not to pay again.
  const [paymentReconciliationRef, setPaymentReconciliationRef] = useState<string | null>(null);
  const [memberCatches, setMemberCatches] = useState<any[]>([]);
  const [leaderboards, setLeaderboards] = useState<Record<string, any>>({});
  const [leaderboardErrors, setLeaderboardErrors] = useState<Record<string, boolean>>({});
  const [member, setMember] = useState<any | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    if (!userId) {
      setLoadError('User not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      // Fetch upcoming competitions (Event model with eventType='COMPETITION')
      // Use yesterday midnight UTC so today's competitions always appear
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      const cutoffISO = yesterday.toISOString();
      const { data: eventsData, errors: eventsErrors } = await client.models.Event.list({
        filter: {
          eventType: { eq: 'COMPETITION' },
          eventDate: { gt: cutoffISO },
          isActive: { eq: true },
        },
      });

      if (eventsErrors) {
        logError('CompetitionsList', eventsErrors);
        setLoadError('Failed to load competitions');
        setIsLoading(false);
        return;
      }

      // Fetch user's pre-registrations
      const { data: regsData, errors: regsErrors } = await client.models.EventPreRegistration.list({
        filter: { memberSub: { eq: userId } },
      });

      if (regsErrors) {
        logError('CompetitionsList', regsErrors);
      }

      // Sort competitions by date
      const sortedComps = (eventsData || []).sort(
        (a: any, b: any) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
      );

      setCompetitions(sortedComps);
      setMyRegistrations(regsData || []);

      // Fetch member record via the narrow server-side projection (replaces
      // Member.list({filter: {cognitoSub}}), which relied on the broad
      // allow.authenticated() model grant).
      try {
        const profileResult: any = await (client.queries as any).getOwnMemberProfile({});
        const profileResponse = parseLambdaResponse(profileResult?.data);
        setMember(profileResponse?.success ? profileResponse.own : null);
      } catch {
        /* skip */
      }

      // Fetch catches
      try {
        let allCatches: any[] = [];
        let nextToken: string | null = null;
        do {
          const result: any = await client.models.CatchSubmission.list({
            filter: { memberSub: { eq: userId } },
            ...(nextToken ? { nextToken } : {}),
          });
          allCatches = allCatches.concat(result.data || []);
          nextToken = result.nextToken ?? null;
        } while (nextToken);
        const approved = allCatches.filter((c: any) => c.status === 'APPROVED');
        setMemberCatches(approved);

        // Fetch leaderboards for published competitions
        const publishedWithCatches = sortedComps.filter(
          (c: any) =>
            isPublished(c) && approved.some((a: any) => a.eventId === c.id)
        );
        const lbs: Record<string, any> = {};
        const lbErrors: Record<string, boolean> = {};
        await Promise.allSettled(
          publishedWithCatches.map(async (comp: any) => {
            try {
              const lb = await (client.queries as any).calculateLeaderboard({ eventId: comp.id });
              lbs[comp.id] = parseLambdaResponse(lb?.data);
            } catch {
              lbErrors[comp.id] = true;
            }
          })
        );
        setLeaderboards(lbs);
        setLeaderboardErrors(lbErrors);
      } catch {
        /* skip */
      }

      setIsLoading(false);
    } catch (error) {
      logError('CompetitionsList', error);
      setLoadError('An error occurred while loading competitions');
      setIsLoading(false);
    }
  };

  const filteredCompetitions = competitions.filter(
    (comp) =>
      comp.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comp.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRegister = async (comp: any) => {
    if (!userId) return;

    setRegistering(comp.id);

    try {
      // Additive narrow API (see .kiro/specs/BACKLOG.md, 18 Jul 2026 security
      // remediation) — replaces the owner-generated EventPreRegistration.create.
      // The server derives memberSub from the caller's JWT, checks
      // preRegistrationEnabled/deadline eligibility and duplicate registration,
      // and forces status: PENDING — none of that is client-controlled anymore.
      const result: any = await (client.mutations as any).createOwnEventPreRegistration({
        eventId: comp.id,
      });
      const response = parseLambdaResponse<{
        success?: boolean;
        preRegistrationId?: string;
        error?: string;
        message?: string;
      }>(result?.data);

      if (!response?.success || !response.preRegistrationId) {
        logError('CompetitionsList', response?.error || 'createOwnEventPreRegistration failed');
        showToast(response?.message || 'Failed to register. Please try again.', 'error');
        setRegistering(null);
        return;
      }

      // Refresh data to show updated registration status
      await fetchData();
      setPendingPaymentReg({ id: response.preRegistrationId, eventId: comp.id });
      showToast('Registration successful! Choose your payment method.', 'success');
    } catch (error) {
      logError('CompetitionsList', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setRegistering(null);
    }
  };

  const isRegistered = (eventId: string) => {
    return myRegistrations.some((reg) => reg.eventId === eventId);
  };

  const getButtonState = (comp: any) => {
    // Backlog #55: this button used to hard-disable in every state below
    // ("Day-of Check-In Only" / "Registration Closed") with no way through,
    // even when the event's own sign-on window (registrationOpenTime
    // through registrationCloseTime, which runs the morning of the comp)
    // was genuinely open right now. A member closing pre-registration on
    // the eve of an event, or landing here once pre-registration is not
    // enabled at all, saw a permanently dead button and no path to
    // register at all — even though MemberDashboard.tsx's equivalent
    // button already had this exact fallback (see its own comment, "Reuse
    // deriveLifecycle — the existing check-in source of truth").
    // This mirrors that fix so the two "Register" buttons for the same
    // competition, on two different member-facing pages, can't disagree
    // about whether registration is actually reachable right now.
    const checkInFallback = (text: string, showMessage: boolean) => {
      const l = deriveLifecycle(comp, new Date());
      const { signOn } = describeLifecycle(comp, new Date(), formatClubTime);
      if (l.signOnOpen) {
        return {
          text: 'Register now →',
          disabled: false,
          className: 'bg-fishing-blue text-white hover:bg-blue-700',
          showMessage: false,
          checkInUrl: '/check-in',
          title: signOn,
        };
      }
      return {
        text,
        disabled: true,
        className: 'bg-gray-100 text-gray-600 cursor-not-allowed',
        showMessage,
        title: signOn,
      };
    };

    // Check if pre-registration is enabled
    if (!comp.preRegistrationEnabled) {
      return checkInFallback('Day-of Check-In Only', true);
    }

    // Timing window — mirrors validateEventEligibility in
    // submit-event-pre-registration/rules.ts by calling the SAME shared helper,
    // so the button state and the server rejection cannot disagree. Entries
    // open 24h before the start and close at the event's sign-on cutoff
    // (registrationCloseTime, defaulting to fishing start + 2h under the
    // canonical rule — NOT at the start itself); after that, entry is via
    // the day-of check-in desk. An absent/unparseable eventDate fails closed
    // on both sides.
    const windowState = getPreRegistrationWindowState(comp.eventDate, new Date(), comp.registrationCloseTime);
    if (windowState === 'NO_DATE') {
      return checkInFallback('Day-of Check-In Only', true);
    }
    if (windowState === 'NOT_YET_OPEN') {
      const opensAt = getPreRegistrationOpensAt(comp.eventDate)!;
      return {
        text: `Opens ${formatClubDate(opensAt, { weekday: 'short', day: 'numeric', month: 'short' })}`,
        disabled: true,
        className: 'bg-gray-100 text-gray-600 cursor-not-allowed',
        showMessage: false,
      };
    }
    if (windowState === 'CLOSED') {
      return checkInFallback('Registration Closed', false);
    }

    // Check if pre-registration deadline has passed
    if (comp.preRegistrationDeadline) {
      const deadline = new Date(comp.preRegistrationDeadline);
      if (new Date() > deadline) {
        return {
          text: 'Pre-Registration Closed',
          disabled: true,
          className: 'bg-gray-100 text-gray-600 cursor-not-allowed',
          showMessage: false,
        };
      }
    }

    if (isRegistered(comp.id)) {
      return {
        text: 'Registered ✓',
        disabled: true,
        className: 'bg-green-100 text-green-800 cursor-not-allowed',
        showMessage: false,
      };
    }

    return {
      text: 'Register',
      disabled: false,
      className: 'bg-fishing-blue text-white hover:bg-blue-700',
      showMessage: false,
    };
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage="competitions" onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Loading competitions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage="competitions" onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{loadError}</p>
            <button
              onClick={fetchData}
              className="mt-4 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MemberNav currentPage="competitions" onNavigate={onNavigate} />

      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all max-w-sm ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Competition pre-registration payment overlay */}
      {(pendingPaymentReg || paymentReconciliationRef) && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Pay Competition Entry Fee</h3>
            {paymentReconciliationRef ? (
              <>
                <PaymentReconciliationNotice paymentReference={paymentReconciliationRef} />
                <button
                  onClick={() => {
                    setPaymentReconciliationRef(null);
                    setPendingPaymentReg(null);
                  }}
                  className="w-full bg-fishing-blue text-white py-2 px-4 rounded font-medium hover:bg-blue-700"
                >
                  Close
                </button>
              </>
            ) : (
              <PaymentChoice
                amountCents={1000}
                amountDisplay="$10.00"
                paymentType="COMPETITION_ENTRY"
                entityId={pendingPaymentReg!.id}
                entityType="EVENT_PRE_REGISTRATION"
                payerEmail={member?.email ?? ''}
                payerName={member ? `${member.firstName} ${member.lastName}` : ''}
                feeKey="competition_fee_member"
                authMode="userPool"
                onSuccess={() => {
                  setPendingPaymentReg(null);
                  showToast('Payment received! See you on competition day.', 'success');
                }}
                onReconciliationRequired={(paymentId) => setPaymentReconciliationRef(paymentId)}
                onPayOnDay={() => {
                  setPendingPaymentReg(null);
                  showToast('Registration confirmed. Pay on the day.', 'success');
                }}
              />
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Competitions</h1>
          <p className="mt-2 text-gray-600">
            Browse and register for upcoming fishing competitions
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search competitions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-fishing-blue focus:border-fishing-blue sm:text-sm"
            />
          </div>
        </div>

        {/* Competitions Grid */}
        {filteredCompetitions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompetitions.map((comp) => {
              const buttonState = getButtonState(comp);

              return (
                <div
                  key={comp.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-fishing-blue to-blue-600 px-6 py-4">
                    <h3 className="text-xl font-semibold text-white">{comp.title}</h3>
                    <p className="text-blue-100 text-sm mt-1">
                      {formatClubDate(comp.eventDate, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>

                  {/* Card Body */}
                  <div className="px-6 py-4">
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-gray-700">
                        <span className="mr-2">📅</span>
                        <span className="text-sm">
                          {formatClubDate(comp.eventDate, {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>

                      {comp.location && (
                        <div className="flex items-center text-gray-700">
                          <span className="mr-2">📍</span>
                          <span className="text-sm">{comp.location}</span>
                        </div>
                      )}

                      {comp.maxAttendees && (
                        <div className="flex items-center text-gray-700">
                          <span className="mr-2">👥</span>
                          <span className="text-sm">Max {comp.maxAttendees} attendees</span>
                        </div>
                      )}

                      {comp.cost !== null && comp.cost !== undefined && (
                        <div className="flex items-center text-gray-700">
                          <span className="mr-2">💰</span>
                          <span className="text-sm">${comp.cost.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {comp.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{comp.description}</p>
                    )}

                    {/* Countdown */}
                    {(() => {
                      const daysToGo = Math.ceil(
                        (new Date(comp.eventDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                      );
                      const l = deriveLifecycle(comp, new Date());
                      return daysToGo > 0 && !l.ended ? (
                        <div className="text-sm text-blue-700 font-medium mb-2">
                          ⏳ {daysToGo} day{daysToGo !== 1 ? 's' : ''} to go
                        </div>
                      ) : null;
                    })()}

                    {/* Placement results for PUBLISHED competitions */}
                    {isPublished(comp) &&
                      memberCatches.some((c: any) => c.eventId === comp.id) && (
                        <div className="mb-3">
                          {leaderboardErrors[comp.id] ? (
                            <p className="text-sm text-gray-500 italic mb-2">Results unavailable</p>
                          ) : leaderboards[comp.id] && member ? (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {Object.entries(leaderboards[comp.id].categoryRankings || {}).map(
                                ([species, entries]: [string, any]) => {
                                  const memberName = `${member.firstName} ${member.lastName}`;
                                  const idx = (entries as any[]).findIndex(
                                    (e: any) => e.competitorName === memberName
                                  );
                                  if (idx < 0) return null;
                                  const pos = idx + 1;
                                  const colour =
                                    pos === 1
                                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                      : pos === 2
                                        ? 'bg-gray-100 text-gray-700 border border-gray-300'
                                        : pos === 3
                                          ? 'bg-orange-100 text-orange-800 border border-orange-300'
                                          : 'text-gray-700';
                                  return (
                                    <span
                                      key={species}
                                      className={`inline-block px-2 py-1 rounded text-xs font-medium ${colour}`}
                                    >
                                      You finished {ordinal(pos)} in {speciesLabel(species)}
                                    </span>
                                  );
                                }
                              )}
                            </div>
                          ) : null}
                          <a
                            href={`/competitions/${comp.id}/results`}
                            onClick={(e: any) => {
                              e.preventDefault();
                              window.history.pushState({}, '', `/competitions/${comp.id}/results`);
                              window.dispatchEvent(new PopStateEvent('popstate'));
                            }}
                            className="text-sm text-fishing-blue hover:underline"
                          >
                            View Results →
                          </a>
                        </div>
                      )}

                    {/* Pre-registration message for day-of only competitions */}
                    {buttonState.showMessage && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                          Registration available on competition day via public check-in only.
                        </p>
                      </div>
                    )}

                    {/* Register Button */}
                    <button
                      onClick={() => {
                        if (buttonState.checkInUrl) {
                          window.location.href = buttonState.checkInUrl;
                          return;
                        }
                        if (!buttonState.disabled) handleRegister(comp);
                      }}
                      disabled={(buttonState.disabled && !buttonState.checkInUrl) || registering === comp.id}
                      title={buttonState.title}
                      className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue ${buttonState.className}`}
                    >
                      {registering === comp.id ? 'Registering...' : buttonState.text}
                    </button>

                    {/* Submit Catch button — shown while catches are open */}
                    {deriveLifecycle(comp, new Date()).catchesOpen && (
                      <button
                        onClick={() => {
                          window.history.pushState({}, '', `/competitions/${comp.id}/submit`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="w-full mt-2 px-4 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        🎣 Submit Catch
                      </button>
                    )}

                    {/* Happy Snaps — shown from fishing start until adjudicated */}
                    {(() => {
                      const l = deriveLifecycle(comp, new Date());
                      return l.started && !l.adjudicated && !l.cancelled;
                    })() && (
                      <button
                        onClick={() => {
                          window.history.pushState({}, '', `/competitions/${comp.id}/happy-snaps`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="w-full mt-2 px-4 py-2 rounded-md text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                      >
                        📸 Happy Snaps
                      </button>
                    )}

                    {/* Photo Wall — shown when published */}
                    {isPublished(comp) && (
                      <a
                        href={`/competitions/${comp.id}/photo-wall`}
                        onClick={(e: any) => {
                          e.preventDefault();
                          window.history.pushState({}, '', `/competitions/${comp.id}/photo-wall`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="w-full mt-2 px-4 py-2 rounded-md text-sm font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors text-center block"
                      >
                        🖼️ Photo Wall
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎣</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No competitions found</h3>
            <p className="text-gray-600">
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Check back soon for upcoming competitions'}
            </p>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 rounded-lg border border-blue-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Competition Registration Info
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Registrations are subject to admin approval</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>You'll receive an email confirmation once your registration is approved</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Check your dashboard for registration status updates</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CompetitionsList;
